// Story 7.4 (ADO-908) — the product image + branded-placeholder renderer used
// by every listing card (browse + search today; a PDP tile later reuses it).
//
// AC1: render the Brand Bank image when a reference exists.
// AC2: render the branded placeholder when there is NO reference OR the image
//      fails to load — never a broken-image icon, never a collapsed box. The
//      error→fallback is guarded against a retry loop: once a src has failed we
//      remember it and render the placeholder instead of the <img>, so the
//      broken URL is never re-attempted.
// AC4: `alt` is the product name for BOTH the real image and the placeholder
//      (the placeholder is an SVG with role="img" + aria-label). A generic
//      "product image" alt is NOT meaningful text (NFR24–26).
// No layout shift (epics.md AC1): the fixed-aspect-ratio box reserves the space
//      before the image resolves, so the card height never depends on load state
//      (CLS = 0). The box is present in EVERY state — image, placeholder, loading.
import { useState } from 'react';
import { ProductImagePlaceholder } from '../../assets/ProductImagePlaceholder';
import styles from './ProductImage.module.css';

export interface ProductImageProps {
  /** Absolute CDN URL of the product image, or `null`/empty for no reference. */
  readonly src: string | null;
  /** Meaningful alternative text — the product name (AC4). */
  readonly alt: string;
  /**
   * Story 7-FU-D (ADO-1217), AC3 — above-the-fold priority. Default `false` keeps
   * `loading="lazy"`; the listing grids opt IN for the first row (the LCP
   * candidate), rendering `loading="eager"` + `fetchpriority="high"`. Positional,
   * so `ProductImage` stays presentational — it never knows its own grid index;
   * the caller decides. Only the real `<img>` is affected (never the placeholder).
   */
  readonly priority?: boolean;
}

export function ProductImage({ src, alt, priority = false }: ProductImageProps) {
  // Track the src that failed (not a bare boolean) so a card re-used for a
  // DIFFERENT product with a new URL is not stuck on the previous failure.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const trimmed = src?.trim() ?? '';
  const showPlaceholder = trimmed === '' || trimmed === failedSrc;
  // AC4: derive the accessible name from the product name. Guard an
  // empty/whitespace name (feed-sourced, non-null but possibly blank) so we
  // never emit alt="" / aria-label="" — that renders the real <img> as
  // decorative and makes the placeholder an axe `svg-img-alt` violation. A
  // generic-but-present label beats a nameless image.
  const label = (alt ?? '').trim() === '' ? 'Product image' : alt;

  return (
    <div className={styles.imageBox}>
      {showPlaceholder ? (
        <ProductImagePlaceholder className={styles.image} role="img" aria-label={label} />
      ) : (
        <img
          className={styles.image}
          src={trimmed}
          alt={label}
          // AC3 — a first-row card renders eager + high fetch priority so the LCP
          // image is not deferred; every other card stays lazy (the default).
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : undefined}
          // Fall back to the placeholder on a load error, once — recording the
          // failed src flips `showPlaceholder` so the <img> unmounts and the
          // broken URL is never requested again (no error→retry loop, AC2).
          onError={() => setFailedSrc(trimmed)}
        />
      )}
    </div>
  );
}
